import { useState, useEffect, useRef } from 'react'
import Filmstrip from './Filmstrip'
import BottomNav from './BottomNav'
import { IconTicket, IconHome, IconHandshake, IconTrophy, IconClapper, IconCueCard, IconSwords, IconCheck } from './icons'
import {
  subscribeToGame,
  createRematchGame,
  acceptRematch,
  declineRematch,
  markPlayerExited,
} from '../utils/firebaseGame'

function formatTime(s) {
  const m   = Math.floor(s / 60).toString().padStart(2, '0')
  const sec = (s % 60).toString().padStart(2, '0')
  return `${m}:${sec}`
}

function determineWinner(p1Result, p2Result) {
  if (!p1Result || !p2Result) return 'tie'
  const hopsDiff = p1Result.hops - p2Result.hops
  if (hopsDiff < 0) return 'p1'
  if (hopsDiff > 0) return 'p2'
  const timeDiff = Math.abs((p1Result.timeSeconds ?? 0) - (p2Result.timeSeconds ?? 0))
  if (timeDiff <= 1) return 'tie'
  return p1Result.timeSeconds < p2Result.timeSeconds ? 'p1' : 'p2'
}

/**
 * Phase E – shared results room shown after both players finish.
 *
 * Props:
 *   gameData   – full Firestore game doc (may be null if fetch failed)
 *   myRole     – 'p1' | 'p2'
 *   tickets    – current ticket count (for rematch affordability)
 *   onRematch  – called with (newGameId, myNewRole) when rematch is accepted
 *   onExit     – called when player leaves to menu
 *   onOpenStore – called to open ticket store
 */
export default function H2HResultsRoom({
  gameData,
  myRole,
  tickets,
  onRematch,
  onExit,
  onOpenStore,
}) {
  // 'idle' | 'requesting' | 'waiting' | 'offered' | 'accepting' | 'declined_by_them'
  const [rematchState, setRematchState] = useState('idle')
  const [rematchConfirmOpen, setRematchConfirmOpen] = useState(false)
  const [newGameId, setNewGameId] = useState(null)
  const exitedRef   = useRef(false)
  const advancedRef = useRef(false)

  const p1Result  = gameData?.p1?.result
  const p2Result  = gameData?.p2?.result
  const myResult  = myRole === 'p1' ? p1Result : p2Result
  const theirResult = myRole === 'p1' ? p2Result : p1Result

  const winner   = determineWinner(p1Result, p2Result)
  const iWon     = winner === myRole
  const itsTie   = winner === 'tie'

  // Subscribe to the game doc to watch for rematch state changes
  useEffect(() => {
    if (!gameData?.id) return

    const unsub = subscribeToGame(gameData.id, (game) => {
      const rematch = game.rematch
      if (!rematch) return

      setNewGameId(rematch.newGameId)

      if (rematch.status === 'accepted' && !advancedRef.current) {
        // Both advance — requester is p1 in new game, opponent is p2
        advancedRef.current = true
        const myNewRole = rematch.requestedByKey === myRole ? 'p1' : 'p2'
        onRematch(rematch.newGameId, myNewRole)
        return
      }

      if (rematch.status === 'declined' && rematch.requestedByKey === myRole) {
        setRematchState('declined_by_them')
        return
      }

      if (rematch.status === 'pending' && rematch.requestedByKey !== myRole) {
        setRematchState('offered')
      }
    })

    return () => unsub()
  }, [gameData?.id, myRole]) // eslint-disable-line react-hooks/exhaustive-deps

  const handleRequestRematch = async () => {
    if (tickets < 1) { onOpenStore(); return }
    setRematchState('requesting')
    try {
      const { newGameId: ngId } = await createRematchGame(gameData.id, myRole)
      setNewGameId(ngId)
      setRematchState('waiting')
    } catch {
      setRematchState('idle')
    }
  }

  const handleAcceptRematch = async () => {
    if (!newGameId) return
    setRematchState('accepting')
    try {
      await acceptRematch(gameData.id, newGameId)
      // Transition handled by the subscription above
    } catch {
      setRematchState('offered')
    }
  }

  const handleDeclineRematch = async () => {
    if (!newGameId) return
    try { await declineRematch(gameData.id, newGameId) } catch {}
    setRematchState('idle')
  }

  const handleExit = async () => {
    if (!exitedRef.current && gameData?.id) {
      exitedRef.current = true
      try { await markPlayerExited(gameData.id, myRole) } catch {}
    }
    onExit()
  }

  // ── Derived display strings ───────────────────────────────────────────────

  const bannerText = itsTie
    ? "IT'S A TIE!"
    : iWon
    ? 'YOU WIN!'
    : 'OPPONENT WINS'
  const BannerIcon = itsTie ? IconHandshake : iWon ? IconTrophy : IconClapper

  const bannerSub = itsTie
    ? 'Dead even — great minds think alike'
    : iWon
    ? 'Nice work, director!'
    : 'Better luck next time'

  const bannerMod = itsTie
    ? 'results-room__banner--tie'
    : iWon
    ? 'results-room__banner--win'
    : 'results-room__banner--lose'

  // Which side highlights gold in hops row
  const myHopsGold   = iWon || itsTie
  const theirHopsGold = !iWon && !itsTie

  return (
    <div className="results-room">

      {/* ── Winner banner ─── */}
      <div className={`results-room__banner ${bannerMod}`}>
        <p className="results-room__banner-title"><BannerIcon size={20} style={{ verticalAlign: 'middle' }} /> {bannerText}</p>
        <p className="results-room__banner-sub">{bannerSub}</p>
      </div>

      {/* ── Stats table ─── */}
      <div className="results-room__stats-table">
        <div className="results-room__stats-header">
          <span>You</span>
          <span></span>
          <span>Opponent</span>
        </div>

        <div className="results-room__stats-row">
          <span className={`results-room__stat-val${myHopsGold ? ' results-room__stat-val--gold' : ''}`}>
            {myResult?.hops ?? '—'}
          </span>
          <span className="results-room__stat-cat">Hops</span>
          <span className={`results-room__stat-val${theirHopsGold ? ' results-room__stat-val--gold' : ''}`}>
            {theirResult?.hops ?? '—'}
          </span>
        </div>

        <div className="results-room__stats-row">
          <span className="results-room__stat-val results-room__stat-val--sm">
            {myResult ? formatTime(myResult.timeSeconds) : '—'}
          </span>
          <span className="results-room__stat-cat">Time</span>
          <span className="results-room__stat-val results-room__stat-val--sm">
            {theirResult ? formatTime(theirResult.timeSeconds) : '—'}
          </span>
        </div>

        {(() => {
          const myCueCards    = myRole === 'p1' ? (gameData?.p1?.cueCardsUsed ?? 0) : (gameData?.p2?.cueCardsUsed ?? 0)
          const theirCueCards = myRole === 'p1' ? (gameData?.p2?.cueCardsUsed ?? 0) : (gameData?.p1?.cueCardsUsed ?? 0)
          if (myCueCards === 0 && theirCueCards === 0) return null
          // Lower count is better (needed fewer hints)
          const myGold    = myCueCards < theirCueCards
          const theirGold = theirCueCards < myCueCards
          const myCls    = `results-room__stat-val results-room__stat-val--cue${myGold ? ' results-room__stat-val--gold' : theirGold ? ' results-room__stat-val--muted' : ''}`
          const theirCls = `results-room__stat-val results-room__stat-val--cue${theirGold ? ' results-room__stat-val--gold' : myGold ? ' results-room__stat-val--muted' : ''}`
          return (
            <div className="results-room__stats-row">
              <span className={myCls}>{myCueCards}</span>
              <span className="results-room__stat-cat"><IconCueCard size={13} style={{ verticalAlign: 'middle' }} /> Cue Cards</span>
              <span className={theirCls}>{theirCueCards}</span>
            </div>
          )
        })()}
      </div>

      {/* ── Film chains ─── */}
      <div className="results-room__chains">
        <div className="results-room__chain-block">
          <p className="results-room__chain-label">Your Path</p>
          {myResult?.chain
            ? <Filmstrip chain={myResult.chain} onItemClick={() => {}} />
            : <p className="results-room__chain-empty">—</p>}
        </div>

        <div className="results-room__chain-block">
          <p className="results-room__chain-label">Their Path</p>
          {theirResult?.chain
            ? <Filmstrip chain={theirResult.chain} onItemClick={() => {}} />
            : <p className="results-room__chain-empty">—</p>}
        </div>
      </div>

      {/* ── Rematch / Exit ─── */}
      <div className="results-room__actions">

        {rematchState === 'idle' && (
          <button
            className="btn-primary results-room__rematch-btn"
            onClick={() => tickets >= 1 ? setRematchConfirmOpen(true) : onOpenStore()}
          >
            Rematch <IconClapper size={14} style={{ verticalAlign: 'middle' }} /> (1 <IconTicket size={13} style={{ verticalAlign: 'middle' }} />)
          </button>
        )}

        {rematchState === 'requesting' && (
          <p className="results-room__rematch-status">Creating rematch…</p>
        )}

        {rematchState === 'waiting' && (
          <p className="results-room__rematch-status results-room__rematch-status--pulse">
            Waiting for opponent to accept…
          </p>
        )}

        {rematchState === 'offered' && (
          <div className="results-room__rematch-offer">
            <p className="results-room__rematch-offered-label"><IconSwords size={15} style={{ verticalAlign: 'middle' }} /> Opponent wants a rematch!</p>
            <div className="results-room__rematch-offer-btns">
              <button
                className="btn-primary"
                onClick={handleAcceptRematch}
                disabled={rematchState === 'accepting'}
              >
                Accept <IconCheck size={14} style={{ verticalAlign: 'middle' }} />
              </button>
              <button className="btn-ghost" onClick={handleDeclineRematch}>
                Decline
              </button>
            </div>
          </div>
        )}

        {rematchState === 'accepting' && (
          <p className="results-room__rematch-status">Accepting…</p>
        )}

        {rematchState === 'declined_by_them' && (
          <p className="results-room__rematch-declined">Opponent declined the rematch.</p>
        )}

        <BottomNav items={[
          { key: 'home', icon: IconHome, label: 'Home', variant: 'primary', onClick: handleExit },
        ]} />

      </div>

      {rematchConfirmOpen && (
        <div className="cue-card-confirm-overlay" onClick={() => setRematchConfirmOpen(false)}>
          <div className="cue-card-confirm-dialog" onClick={(e) => e.stopPropagation()}>
            <p className="cue-card-confirm-dialog__title">Rematch</p>
            <p className="cue-card-confirm-dialog__msg">
              Challenge your opponent to another round.
            </p>
            <p className="cue-card-confirm-dialog__cost">
              <IconTicket size={13} aria-hidden="true" />
              <span>1 Ticket · You have {tickets.toLocaleString()}</span>
            </p>
            <div className="cue-card-confirm-dialog__actions">
              <button
                className="btn-primary"
                onClick={() => { setRematchConfirmOpen(false); handleRequestRematch() }}
              >
                Rematch
              </button>
              <button className="btn-ghost" onClick={() => setRematchConfirmOpen(false)}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
